/**
 * 🎭 BIG FACE GUIDES - Guides IA avec Grands Visages
 * 
 * Design "Portrait Cinématique" - Les guides prennent de la place !
 * - Grand portrait (~250-300px) au centre
 * - Expressions qui changent en temps réel
 * - Zone de chat compacte en dessous
 * - Effet de présence immersif
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Send, ChevronLeft, Sparkles, Heart, X, MessageCircle,
  Moon, Sun, Loader2, Maximize2, Minimize2
} from 'lucide-react';
import { toast } from 'sonner';
import { GUIDE_IMAGES, GUIDE_CONFIG } from './soul/GuideAvatar';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// 🎨 Configuration étendue des guides avec plus d'expressions
const EXTENDED_GUIDE_CONFIG = {
  luna: {
    ...GUIDE_CONFIG.luna,
    tagline: 'Les étoiles te guident vers l\'amour...',
    gradient: 'from-purple-600 via-indigo-600 to-purple-800',
    accentColor: '#8B5CF6',
    speciality: ['Émotions', 'Intuition', 'Guérison'],
    defaultExpression: 'welcome_smile'
  },
  enzo: {
    ...GUIDE_CONFIG.enzo,
    tagline: 'La confiance, ça se travaille !',
    gradient: 'from-red-500 via-orange-500 to-amber-500',
    accentColor: '#EF4444',
    speciality: ['Confiance', 'Action', 'Séduction'],
    defaultExpression: 'welcome_smile'
  },
  nyx: {
    ...GUIDE_CONFIG.nyx,
    tagline: 'Les cœurs brisés font les plus belles histoires',
    gradient: 'from-gray-800 via-gray-900 to-black',
    accentColor: '#6B7280',
    speciality: ['Red flags', 'Ruptures', 'Protection'],
    defaultExpression: 'confident_smirk'
  },
  kurson: {
    ...GUIDE_CONFIG.kurson,
    tagline: 'La chance sourit aux audacieux',
    gradient: 'from-yellow-500 via-amber-500 to-orange-500',
    accentColor: '#F59E0B',
    speciality: ['Timing', 'Malchance', 'Philosophie'],
    defaultExpression: 'knowing_smirk'
  }
};

// 🖼️ Mapping des expressions vers les images
const getGuideImage = (guideId, expression) => {
  const images = GUIDE_IMAGES[guideId];
  if (!images) return null;
  return images[expression] || images.default || images.welcome_smile;
};

/**
 * 🎭 BigFaceGuide - Un guide avec un GRAND visage
 */
export function BigFaceGuide({ 
  guideId, 
  customer, 
  profile, 
  isDarkMode,
  onClose,
  fullScreen = false 
}) {
  const config = EXTENDED_GUIDE_CONFIG[guideId] || EXTENDED_GUIDE_CONFIG.luna;
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentExpression, setCurrentExpression] = useState(config.defaultExpression);
  const [isExpanded, setIsExpanded] = useState(false);
  const messagesEndRef = useRef(null);

  // Image actuelle du guide
  const currentImage = getGuideImage(guideId, currentExpression);

  // Charger l'historique
  useEffect(() => {
    loadConversation();
  }, [guideId, customer?.id]);

  const loadConversation = async () => {
    if (!customer?.id) return;
    
    try {
      const res = await fetch(`${API_URL}/api/guides/conversation/${guideId}/${customer.id}`);
      if (res.ok) {
        const data = await res.json();
        if (data.messages) {
          setMessages(data.messages);
        }
      }
    } catch (err) {
      console.error('Erreur chargement conversation:', err);
    }
  };

  // Scroll auto
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Animation du visage selon le message
  const animateFace = (mood) => {
    const moodToExpression = {
      thinking: 'mysterious_thinking',
      happy: 'warm_laugh',
      flirty: 'seductive_gaze',
      supportive: 'tender_support',
      curious: 'curious_look',
      excited: 'excited_look',
      romantic: 'romantic_gaze',
      wink: 'wink_playful',
      default: 'welcome_smile'
    };

    const expression = moodToExpression[mood] || moodToExpression.default;
    
    // Vérifier si l'expression existe pour ce guide
    if (GUIDE_IMAGES[guideId]?.[expression]) {
      setCurrentExpression(expression);
    }
  };

  // Envoyer un message
  const sendMessage = async () => {
    if (!inputText.trim() || isLoading) return;
    
    const userMessage = inputText.trim();
    setInputText('');
    setIsLoading(true);
    
    // Ajouter le message utilisateur
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    }]);
    
    // Expression de réflexion
    animateFace('thinking');
    
    try {
      const res = await fetch(`${API_URL}/api/guides/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guideId,
          customerId: customer?.id || 'anonymous',
          customerName: customer?.firstName || profile?.displayName || 'Ami(e)',
          message: userMessage,
          psycheProfile: null
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        
        // Ajouter la réponse
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.response,
          timestamp: new Date().toISOString()
        }]);
        
        // Animer le visage selon la réponse
        setTimeout(() => animateFace(data.mood || 'happy'), 500);
      } else {
        throw new Error('Erreur API');
      }
    } catch (err) {
      console.error('Erreur envoi message:', err);
      toast.error(`${config.name} a du mal à répondre...`);
      animateFace('supportive');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div 
      className={`relative flex flex-col ${fullScreen ? 'fixed inset-0 z-50' : 'rounded-2xl overflow-hidden'}`}
      style={{
        background: isDarkMode 
          ? 'linear-gradient(180deg, #0a0a0f 0%, #1a1a25 100%)' 
          : 'linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)',
        minHeight: fullScreen ? '100vh' : '600px'
      }}
      data-testid={`big-face-guide-${guideId}`}
    >
      {/* === SECTION PORTRAIT (Grande partie de l'écran) === */}
      <div 
        className={`relative flex-shrink-0 overflow-hidden transition-all duration-300`}
        style={{ 
          height: isExpanded ? '60%' : '45%',
          minHeight: '280px'
        }}
      >
        {/* Fond dégradé */}
        <div 
          className={`absolute inset-0 bg-gradient-to-br ${config.gradient}`}
          style={{
            boxShadow: `inset 0 -50px 100px ${isDarkMode ? 'rgba(0,0,0,0.8)' : 'rgba(0,0,0,0.3)'}`
          }}
        />

        {/* Particules décoratives */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(15)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 rounded-full bg-white/40"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -30, 0],
                opacity: [0.2, 0.8, 0.2],
                scale: [0.5, 1.2, 0.5],
              }}
              transition={{
                duration: 3 + Math.random() * 3,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>

        {/* Header avec contrôles */}
        <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between z-10">
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 rounded-full bg-black/30 hover:bg-black/50 transition-colors"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
          )}
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2 rounded-full bg-black/30 hover:bg-black/50 transition-colors"
            >
              {isExpanded ? (
                <Minimize2 className="w-5 h-5 text-white" />
              ) : (
                <Maximize2 className="w-5 h-5 text-white" />
              )}
            </button>
            
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 rounded-full bg-black/30 hover:bg-black/50 transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            )}
          </div>
        </div>

        {/* 🎭 LE GRAND VISAGE */}
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div
            className="relative"
            animate={{ 
              y: [0, -8, 0],
            }}
            transition={{ 
              duration: 4, 
              repeat: Infinity, 
              ease: "easeInOut" 
            }}
          >
            {/* Glow effect */}
            <div 
              className="absolute inset-0 rounded-full blur-3xl opacity-50"
              style={{ 
                background: config.accentColor,
                transform: 'scale(1.3)'
              }}
            />
            
            {/* Image du guide */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentExpression}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3 }}
                className="relative"
              >
                {currentImage ? (
                  <img
                    src={currentImage}
                    alt={config.name}
                    className="w-64 h-64 md:w-72 md:h-72 rounded-full object-cover shadow-2xl border-4 border-white/20"
                    style={{
                      boxShadow: `0 0 60px ${config.accentColor}40, 0 20px 40px rgba(0,0,0,0.4)`
                    }}
                  />
                ) : (
                  <div 
                    className={`w-64 h-64 md:w-72 md:h-72 rounded-full bg-gradient-to-br ${config.gradient} flex items-center justify-center text-8xl border-4 border-white/20`}
                  >
                    {guideId === 'luna' ? '🌙' : guideId === 'enzo' ? '🔥' : guideId === 'nyx' ? '🖤' : '🎰'}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>

        {/* Info du guide en bas */}
        <div className="absolute bottom-0 left-0 right-0 p-4 text-center">
          <h2 className="text-3xl font-black text-white drop-shadow-lg">
            {config.name}
          </h2>
          <p className="text-white/70 text-sm italic">
            &ldquo;{config.tagline}&rdquo;
          </p>
          
          {/* Tags de spécialité */}
          <div className="flex flex-wrap justify-center gap-2 mt-2">
            {config.speciality?.map((spec, i) => (
              <span 
                key={i}
                className="px-3 py-1 rounded-full text-xs bg-white/20 text-white backdrop-blur-sm"
              >
                {spec}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* === SECTION CHAT === */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Messages */}
        <div 
          className="flex-1 overflow-y-auto p-4 space-y-3"
          style={{ maxHeight: '250px' }}
        >
          {/* Message d'accueil si vide */}
          {messages.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`p-4 rounded-xl text-center ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-100'}`}
            >
              <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                👋 Salut ! Je suis <strong>{config.name}</strong>.
              </p>
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {config.personality}
              </p>
            </motion.div>
          )}

          {/* Liste des messages */}
          {messages.map((msg, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] p-3 rounded-2xl ${
                  msg.role === 'user'
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-br-sm'
                    : isDarkMode 
                      ? 'bg-gray-800 text-white rounded-bl-sm'
                      : 'bg-white shadow-md text-gray-800 rounded-bl-sm'
                }`}
              >
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
              </div>
            </motion.div>
          ))}

          {/* Indicateur de frappe */}
          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center gap-2"
            >
              <div className={`px-4 py-2 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white shadow'}`}>
                <div className="flex gap-1">
                  {[0, 1, 2].map(i => (
                    <motion.div
                      key={i}
                      className={`w-2 h-2 rounded-full ${isDarkMode ? 'bg-gray-500' : 'bg-gray-400'}`}
                      animate={{ y: [0, -5, 0] }}
                      transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.2 }}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Zone de saisie */}
        <div className={`p-4 border-t ${isDarkMode ? 'border-gray-800 bg-gray-900/80' : 'border-gray-200 bg-white/80'} backdrop-blur-lg`}>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={`Parle à ${config.name}...`}
              className={`flex-1 px-4 py-3 rounded-xl border-0 focus:ring-2 focus:ring-purple-500 ${
                isDarkMode 
                  ? 'bg-gray-800 text-white placeholder-gray-500' 
                  : 'bg-gray-100 text-gray-800 placeholder-gray-400'
              }`}
              disabled={isLoading}
            />
            <motion.button
              onClick={sendMessage}
              disabled={!inputText.trim() || isLoading}
              className={`p-3 rounded-xl font-medium transition-all ${
                inputText.trim() && !isLoading
                  ? `bg-gradient-to-r ${config.gradient} text-white shadow-lg hover:shadow-xl`
                  : isDarkMode ? 'bg-gray-800 text-gray-600' : 'bg-gray-200 text-gray-400'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </motion.button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * 🎯 BigFaceGuidesSelector - Sélecteur avec grands portraits
 */
export function BigFaceGuidesSelector({ 
  customer, 
  profile, 
  isDarkMode,
  onSelectGuide 
}) {
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [hoveredGuide, setHoveredGuide] = useState(null);

  if (selectedGuide) {
    return (
      <BigFaceGuide
        guideId={selectedGuide}
        customer={customer}
        profile={profile}
        isDarkMode={isDarkMode}
        onClose={() => setSelectedGuide(null)}
      />
    );
  }

  const guides = Object.entries(EXTENDED_GUIDE_CONFIG);
  // Séparer les guides Lumière (Luna, Enzo) et Ombre (Nyx, Kurson)
  const lightGuides = guides.filter(([id]) => id === 'luna' || id === 'enzo');
  const darkGuides = guides.filter(([id]) => id === 'nyx' || id === 'kurson');

  return (
    <div className={`space-y-6 p-4 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`} data-testid="big-face-guides-selector">
      {/* Header */}
      <div className="text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white mb-3"
        >
          <Sparkles className="w-5 h-5" />
          <span className="font-bold">Tes Guides</span>
        </motion.div>
        
        <h2 className={`text-2xl font-black ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Choisis ton Guide
        </h2>
        <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Mode Clair ou Mode Ombre ?
        </p>
      </div>

      {/* 🌟 SECTION LUMIÈRE - Luna & Enzo */}
      <div className="space-y-3">
        <h3 className={`text-lg font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          <Sun className="w-5 h-5 text-amber-400" />
          Mode Lumière
        </h3>
        
        <div className="grid grid-cols-2 gap-3">
          {lightGuides.map(([id, guide]) => {
            const image = getGuideImage(id, guide.defaultExpression);
            const isHovered = hoveredGuide === id;
            
            return (
              <motion.button
                key={id}
                onClick={() => setSelectedGuide(id)}
                onMouseEnter={() => setHoveredGuide(id)}
                onMouseLeave={() => setHoveredGuide(null)}
                className="relative overflow-hidden rounded-2xl"
                style={{
                  boxShadow: isHovered ? `0 0 30px ${guide.accentColor}40` : 'none',
                  minHeight: '200px'
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {/* Background gradient */}
                <div className={`absolute inset-0 bg-gradient-to-br ${guide.gradient}`} />
                
                {/* GRAND VISAGE - 50% de la carte */}
                <div className="relative h-full flex flex-col items-center justify-center py-4">
                  <motion.div
                    animate={{ y: isHovered ? -5 : 0 }}
                    className="relative"
                  >
                    {/* Glow effect */}
                    <div 
                      className="absolute inset-0 rounded-full blur-2xl opacity-50"
                      style={{ background: guide.accentColor, transform: 'scale(1.2)' }}
                    />
                    
                    {image ? (
                      <img
                        src={image}
                        alt={guide.name}
                        className="relative w-24 h-24 md:w-32 md:h-32 rounded-full object-cover"
                        style={{
                          boxShadow: `0 0 40px ${guide.accentColor}50, 0 10px 30px rgba(0,0,0,0.4)`
                        }}
                      />
                    ) : (
                      <div className={`w-24 h-24 md:w-32 md:h-32 rounded-full bg-gradient-to-br ${guide.gradient} flex items-center justify-center text-5xl`}>
                        {id === 'luna' ? '🌙' : '🔥'}
                      </div>
                    )}
                  </motion.div>
                  
                  {/* Nom */}
                  <h4 className="mt-3 font-bold text-white text-lg">{guide.name}</h4>
                  <p className="text-white/70 text-xs">{guide.role}</p>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* 🌙 SECTION OMBRE - Nyx & Kurson */}
      <div className="space-y-3">
        <h3 className={`text-lg font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          <Moon className="w-5 h-5 text-purple-400" />
          Mode Ombre
        </h3>
        
        <div className="grid grid-cols-2 gap-3">
          {darkGuides.map(([id, guide]) => {
            const image = getGuideImage(id, guide.defaultExpression);
            const isHovered = hoveredGuide === id;
            
            return (
              <motion.button
                key={id}
                onClick={() => setSelectedGuide(id)}
                onMouseEnter={() => setHoveredGuide(id)}
                onMouseLeave={() => setHoveredGuide(null)}
                className="relative overflow-hidden rounded-2xl"
                style={{
                  boxShadow: isHovered ? `0 0 30px ${guide.accentColor}40` : 'none',
                  minHeight: '200px'
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {/* Background gradient sombre */}
                <div className={`absolute inset-0 bg-gradient-to-br ${guide.gradient}`} />
                
                {/* GRAND VISAGE */}
                <div className="relative h-full flex flex-col items-center justify-center py-4">
                  <motion.div
                    animate={{ y: isHovered ? -5 : 0 }}
                    className="relative"
                  >
                    {/* Glow effect sombre */}
                    <div 
                      className="absolute inset-0 rounded-full blur-2xl opacity-40"
                      style={{ background: guide.accentColor, transform: 'scale(1.2)' }}
                    />
                    
                    {image ? (
                      <img
                        src={image}
                        alt={guide.name}
                        className="relative w-24 h-24 md:w-32 md:h-32 rounded-full object-cover"
                        style={{
                          boxShadow: `0 0 40px ${guide.accentColor}40, 0 10px 30px rgba(0,0,0,0.6)`
                        }}
                      />
                    ) : (
                      <div className={`w-24 h-24 md:w-32 md:h-32 rounded-full bg-gradient-to-br ${guide.gradient} flex items-center justify-center text-5xl`}>
                        {id === 'nyx' ? '🖤' : '🔮'}
                      </div>
                    )}
                  </motion.div>
                  
                  {/* Nom */}
                  <h4 className="mt-3 font-bold text-white text-lg">{guide.name}</h4>
                  <p className="text-white/70 text-xs">{guide.role}</p>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Message en bas */}
      <div className={`p-3 rounded-xl text-center ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-100'}`}>
        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          💜 Tes guides t'accompagnent dans ta quête amoureuse
        </p>
      </div>
    </div>
  );
}

export default BigFaceGuidesSelector;
